import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { TRAINING_FIELDS } from "@/lib/trainingFields";

const inputCls = "bg-[#0A0A0A] border-[#27272A] rounded-none focus:border-[#15803D] focus:ring-1 focus:ring-[#15803D]";
const labelCls = "text-xs font-bold uppercase tracking-[0.15em] text-[#A1A1AA]";

const today = () => new Date().toISOString().slice(0, 10);
const blank = () => ({ date: today(), note: "" });

export default function TrainingForm({ open, onOpenChange, playerId, test, onSaved }) {
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(test);

  useEffect(() => {
    if (test) {
      const f = { date: test.date, note: test.note || "" };
      TRAINING_FIELDS.forEach(({ k }) => {
        f[k] = test[k] ?? "";
      });
      setForm(f);
    } else {
      setForm(blank());
    }
  }, [test, open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const num = (v) => (v === "" || v === undefined ? null : Number(v));
    const payload = { date: form.date, note: form.note || null };
    TRAINING_FIELDS.forEach(({ k }) => {
      payload[k] = num(form[k]);
    });
    try {
      if (isEdit) await api.put(`/players/${playerId}/training/${test.id}`, payload);
      else await api.post(`/players/${playerId}/training`, payload);
      toast.success(isEdit ? "Test updated" : "Training test logged");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-[#27272A] rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black uppercase tracking-tight">
            {isEdit ? "Edit Training Test" : "Log Training Test"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5" data-testid="training-form">
          <div className="space-y-1.5 max-w-[200px]">
            <Label className={labelCls}>Test Date</Label>
            <Input data-testid="training-date-input" type="date" required value={form.date} onChange={set("date")} className={inputCls} />
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#71717A] mb-3">Combine Metrics</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {TRAINING_FIELDS.map(({ k, l }) => (
                <div key={k} className="space-y-1">
                  <Label className="text-[11px] text-[#A1A1AA]">{l}</Label>
                  <Input data-testid={`training-${k}-input`} type="number" step="any" value={form[k] ?? ""} onChange={set(k)} className={`${inputCls} h-9`} />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className={labelCls}>Coach Note</Label>
            <Textarea value={form.note} onChange={set("note")} rows={2} className={inputCls} />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
            <Button type="submit" data-testid="training-save-button" disabled={saving} className="rounded-none bg-[#15803D] hover:bg-[#166534] font-bold uppercase tracking-wider">
              {saving ? "Saving..." : isEdit ? "Update Test" : "Save Test"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
